import { useMemo } from 'react';

import {
  SIDEBAR_PAGES_TO_LOAD,
  useGetInboxNotificationsQuery,
} from 'src/services/api/inboxApi';

import { useAppSelector } from '../useStore';

/**
 * Fetches the notification count for each installed app/extension/embed.
 * When the notifications api is used to create a notification with an api key
 * associated with an appId, that notification will be displayed with the associated app.
 */
export const useAppNotificationCounts = (): Record<string, number> => {
  const isClient = useAppSelector((state) => state.user.isClient);
  const activeCompanyId = useAppSelector(
    (state) => state.activeCompany.activeCompanyId,
  );

  // ignore the query if the user is not a client, because the
  // app notification badge is only relevant to clients. IUs see
  // their notifications in the inbox.
  const { data: notifications } = useGetInboxNotificationsQuery(
    SIDEBAR_PAGES_TO_LOAD,
    {
      skip: !isClient,
    },
  );

  return useMemo(() => {
    // similar to comment above, IUs see notifications
    // in in inbox so AppNotificationBadge should not
    // be rendered for them.
    if (!isClient || !notifications) return {};

    const counts: Record<string, number> = {};
    notifications.forEach((notification) => {
      if (notification.companyId !== activeCompanyId) {
        return;
      }
      if (
        notification.event === 'customApp.action' &&
        notification.appId &&
        !notification.isRead
      ) {
        counts[notification.appId] = (counts[notification.appId] || 0) + 1;
      }
    });
    return counts;
  }, [notifications, isClient, activeCompanyId]);
};
